import { EXAMPLES_FILTER_LABEL, SCORING_FILTER_LABEL } from '@/lib/criterion-labels';
import { CRITERION_WORDING } from '@/lib/criterion-wording';
import type { CriterionFilters } from '@matter_extractor/shared';
import {
  criterionExamplesFilterSchema,
  criterionScoringFilterSchema,
} from '@matter_extractor/shared';
import { Select } from '@pms-digital/design-system';

/**
 * The criterion's OWN filters, at the head of « Plus de filtres » (refonte
 * modulaire §6).
 *
 * TWO controls, and they are the only ones of the whole panel that are not
 * shared by the four versioned objects: how many positive examples feed the
 * scribe, and whether the criterion is scored. §4 gives the rest - the two
 * periods and the four boxes - to every library, so they live in
 * `versioned/filter-panel.tsx` and this file fills its slot.
 *
 * They sit FIRST in the row because they are what somebody opened the panel
 * for; a persona puts its « présentation » and its « cran » in the same place.
 *
 * « Notation » is a filter and not a sort (§6), which is why nothing about it
 * reaches `SORT_LABEL`: « toutes, notés, non notés » answers a question about
 * ONE criterion, where a sort would have to rank two of them by a scale whose
 * band count means nothing next to another's.
 */

export interface CriterionFilterFieldsProps {
  filters: CriterionFilters;
  onChange: (patch: Partial<CriterionFilters>) => void;
}

export function CriterionFilterFields({ filters, onChange }: CriterionFilterFieldsProps) {
  // The test ids sit on wrappers: the package `Select` takes none (its trigger
  // is the `combobox` inside). Both filters carry their neutral choice as `all`,
  // so no empty value has to cross the select.
  return (
    <>
      <div data-testid={`${CRITERION_WORDING.entity}-filtre-exemples`} className="w-40 max-w-full">
        <Select
          snug
          label="Exemples"
          value={filters.examples}
          onValueChange={(value) => {
            const parsed = criterionExamplesFilterSchema.safeParse(value);
            if (parsed.success) onChange({ examples: parsed.data });
          }}
          options={criterionExamplesFilterSchema.options.map((option) => ({
            value: option,
            label: EXAMPLES_FILTER_LABEL[option],
          }))}
        />
      </div>

      <div data-testid={`${CRITERION_WORDING.entity}-filtre-notation`} className="w-40 max-w-full">
        <Select
          snug
          label="Notation"
          value={filters.scoring}
          onValueChange={(value) => {
            const parsed = criterionScoringFilterSchema.safeParse(value);
            if (parsed.success) onChange({ scoring: parsed.data });
          }}
          options={criterionScoringFilterSchema.options.map((option) => ({
            value: option,
            label: SCORING_FILTER_LABEL[option],
          }))}
        />
      </div>
    </>
  );
}
